/**
 * QueueMonitor - Queue statistics table with pause/resume actions
 *
 * Features:
 * - Table showing queue stats (pending, processing, failed, completed)
 * - Pause/Resume actions per queue
 * - Visual status indicators
 * - Loading and empty states
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { Button } from '@respectly/ui';
import { motion } from 'framer-motion';
import {
  Inbox,
  Pause,
  Play,
  AlertTriangle,
  Clock,
  Loader2,
  ListOrdered,
} from 'lucide-react';
import type { QueueStats } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface QueueMonitorProps {
  queues: QueueStats[];
  isLoading?: boolean;
  isPausingQueue?: string | null;
  isResumingQueue?: string | null;
  onPauseQueue?: (name: string) => void;
  onResumeQueue?: (name: string) => void;
  className?: string;
}

// ============================================
// Helpers
// ============================================

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

function formatLastProcessed(date: Date | null | undefined): string {
  if (!date) return 'Never';
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) return `${seconds}s ago`;
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return new Date(date).toLocaleDateString();
}

// ============================================
// Component
// ============================================

export function QueueMonitor({
  queues,
  isLoading = false,
  isPausingQueue = null,
  isResumingQueue = null,
  onPauseQueue,
  onResumeQueue,
  className,
}: QueueMonitorProps) {
  const totalPending = queues.reduce((sum, q) => sum + q.pending, 0);
  const totalFailed = queues.reduce((sum, q) => sum + q.failed, 0);
  const pausedCount = queues.filter((q) => q.paused).length;

  return (
    <Card className={cn('min-w-0 border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900', className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <ListOrdered className="h-4.5 w-4.5 text-white" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base truncate">Queue Monitor</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Background job processing status
              </p>
            </div>
          </div>

          {/* Summary Pills */}
          <div className="flex flex-wrap items-center gap-2">
            {totalPending > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20">
                <Clock className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                  {formatNumber(totalPending)} pending
                </span>
              </div>
            )}
            {totalFailed > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20">
                <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                <span className="text-xs font-medium text-red-700 dark:text-red-400">
                  {formatNumber(totalFailed)} failed
                </span>
              </div>
            )}
            {pausedCount > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/20">
                <Pause className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                  {pausedCount} paused
                </span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 min-w-0 overflow-x-auto">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 animate-pulse"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-700" />
                  <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
                <div className="flex items-center gap-6">
                  <div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-8 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : queues.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
              <Inbox className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No queues configured
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {queues.map((queue) => {
              const isActionInProgress =
                isPausingQueue === queue.name || isResumingQueue === queue.name;

              return (
                <motion.div
                  key={queue.name}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={cn(
                    'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 rounded-lg min-w-0',
                    'bg-slate-50 dark:bg-slate-800/50',
                    'border border-transparent',
                    queue.paused && 'border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10'
                  )}
                >
                  {/* Queue Info */}
                  <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
                    <div
                      className={cn(
                        'h-8 w-8 flex-shrink-0 rounded-lg flex items-center justify-center',
                        queue.paused
                          ? 'bg-amber-100 dark:bg-amber-900/30'
                          : 'bg-slate-200 dark:bg-slate-700'
                      )}
                    >
                      {queue.paused ? (
                        <Pause className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      ) : (
                        <Play className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                        {queue.displayName}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Last: {formatLastProcessed(queue.lastProcessedAt)}
                      </p>
                    </div>
                  </div>

                  {/* Stats + Action */}
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 md:gap-6 flex-shrink-0">
                    <div className="hidden sm:block text-center min-w-[52px]">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">
                        {formatNumber(queue.pending)}
                      </p>
                      <p className="text-xs text-slate-500">Pending</p>
                    </div>
                    <div className="hidden sm:block text-center min-w-[52px]">
                      <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 tabular-nums">
                        {formatNumber(queue.processing)}
                      </p>
                      <p className="text-xs text-slate-500">Processing</p>
                    </div>
                    <div className="text-center min-w-[52px]">
                      <p
                        className={cn(
                          'text-sm font-semibold tabular-nums',
                          queue.failed > 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-slate-400'
                        )}
                      >
                        {formatNumber(queue.failed)}
                      </p>
                      <p className="text-xs text-slate-500">Failed</p>
                    </div>
                    <div className="hidden md:block text-center min-w-[52px]">
                      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                        {formatNumber(queue.completed)}
                      </p>
                      <p className="text-xs text-slate-500">Completed</p>
                    </div>

                    {/* Action Button - always visible and tappable */}
                    <Button
                      variant={queue.paused ? 'default' : 'outline'}
                      size="sm"
                      disabled={isActionInProgress}
                      onClick={() =>
                        queue.paused
                          ? onResumeQueue?.(queue.name)
                          : onPauseQueue?.(queue.name)
                      }
                      className="min-w-[80px] flex-shrink-0 touch-manipulation"
                    >
                      {isActionInProgress ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : queue.paused ? (
                        <>
                          <Play className="h-3.5 w-3.5 mr-1.5" />
                          Resume
                        </>
                      ) : (
                        <>
                          <Pause className="h-3.5 w-3.5 mr-1.5" />
                          Pause
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
